import LoadingSpinner from '@/components/LoadingSpinner'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useSupabaseSession } from '@/lib/supabase/supabaseProvider'
import { errorToast } from '@/lib/utils/ToastUtils'
import { trpc } from '@/lib/trpc/client'
import { trpcVanilla } from '@/lib/trpc/vanilla'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { requirePermission } from '@/lib/permissions/permissions'
import { format } from 'date-fns'
import {
  AlertTriangle,
  Clock,
  CreditCard,
  ExternalLink,
  Tag,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { getActiveOffer } from '@/lib/billing/offers'
import { useOrgEntitlements } from '@/lib/hooks/useOrgEntitlements'
import { PLAN_LABELS, PlanKey } from '@/lib/billing/plans'

export const Route = createFileRoute(
  '/_auth/_dashboard/settings/_layout/subscription',
)({
  beforeLoad: () => {
    requirePermission('billing.manage')
  },
  component: SubscriptionPage,
})
type PlanDetails = {
  name: string | null
  amount: number | null
  currency: string | null
  interval: string | null
  original_amount?: number | null
  discount?: {
    name?: string | null
    percent_off?: number | null
    amount_off?: number | null
    duration?: string | null
    duration_in_months?: number | null
    months_remaining?: number | null
    discount_started_at?: string | null
    valid?: boolean | null
  } | null
}
const cacheTtlMs = 5 * 60 * 1000

const fallbackPlan: PlanDetails = {
  name: 'Trainrr Pro',
  amount: 35,
  currency: 'USD',
  interval: 'month',
  original_amount: null,
  discount: null,
}

function SubscriptionPage() {
  const [loading, setLoading] = useState(false)
  const { subscription } = useSupabaseSession()
  const navigate = useNavigate()
  const [upgradeLoading, setUpgradeLoading] = useState(false)
  const { data: entitlements } = useOrgEntitlements()
  const metadata =
    subscription?.metadata && typeof subscription.metadata === 'object'
      ? (subscription.metadata as Record<string, unknown>)
      : null
  const isInternalTrialSubscription = Boolean(
    !subscription?.price_id &&
      (metadata?.internal_trial === true ||
        metadata?.trial_source === 'internal'),
  )

  const {
    data: billingDetails,
    isLoading: billingLoading,
    isFetching: billingFetching,
    error: billingError,
  } = trpc.billing.details.useQuery(undefined, {
    staleTime: cacheTtlMs,
    refetchOnWindowFocus: true,
  })

  const isBillingLoading = billingLoading || billingFetching
  const resolvedPlan = billingDetails?.plan ?? fallbackPlan
  const discount = billingDetails?.plan?.discount
  const invoices = billingDetails?.invoices ?? []
  const paymentMethod = billingDetails?.payment_method

  const planStatus = subscription?.status ?? 'inactive'
  const statusLabel =
    planStatus === 'trialing'
      ? 'Trial'
      : planStatus === 'active'
        ? 'Active'
        : planStatus === 'past_due'
          ? 'Past due'
          : 'Inactive'
  const statusBadgeClass =
    planStatus === 'active' || planStatus === 'trialing'
      ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
      : planStatus === 'past_due'
        ? 'bg-red-100 text-red-800 border-red-200'
        : 'bg-muted text-muted-foreground border-border'
  const renewalLabel =
    planStatus === 'trialing'
      ? paymentMethod?.last4 || subscription?.has_payment_method
        ? 'First payment on'
        : 'Trial ends'
      : 'Next renewal'
  const renewalDate =
    planStatus === 'trialing'
      ? subscription?.trial_end
      : subscription?.current_period_end
  const needsPaymentMethod =
    planStatus === 'trialing' &&
    !isBillingLoading &&
    !paymentMethod?.last4 &&
    !subscription?.has_payment_method

  const formatCurrency = (
    amount?: number | null,
    currencyOverride?: string | null,
  ) => {
    if (amount == null) return '-'
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyOverride ?? resolvedPlan.currency ?? 'USD',
        maximumFractionDigits: 2,
      }).format(amount)
    } catch {
      return `${currencyOverride ?? resolvedPlan.currency ?? 'USD'} ${amount.toFixed(2)}`
    }
  }

  useEffect(() => {
    if (billingError instanceof Error) {
      errorToast(billingError, billingError.message)
    }
  }, [billingError])

  useEffect(() => {
    if (!isInternalTrialSubscription) return
    navigate({ to: '/settings', replace: true })
  }, [isInternalTrialSubscription, navigate])

  if (isInternalTrialSubscription) {
    return null
  }

  const handleAddPaymentMethod = async () => {
    setUpgradeLoading(true)
    const offer = getActiveOffer()
    const planKey = (
      entitlements?.plan_key && entitlements.plan_key !== 'pro_monthly'
        ? entitlements.plan_key
        : 'growth_monthly'
    ) as PlanKey
    try {
      const data = await trpcVanilla.billing.upgradeTrial.mutate({
        redirect: window.location.href,
        offer,
        planKey,
      })

      const url = data.session?.url
      if (!url) {
        console.error('No session url')
        setUpgradeLoading(false)
        return
      }

      window.location.replace(url)
    } catch (error) {
      console.error('Upgrade trial function error', error)
      errorToast(error as Error)
      setUpgradeLoading(false)
    }
  }
  const getSession = async () => {
    setLoading(true)
    try {
      const data = await trpcVanilla.billing.portalSession.mutate({
        redirect: window.location.href,
      })

      const url = data.session.url
      if (!url) {
        console.error('No session url')
        setLoading(false)
        return
      }

      window.location.replace(url)
    } catch (error) {
      console.error('Function returned an error', error)
      errorToast(error as Error)
      setLoading(false)
    }
  }

  return (
    <div className="ph-no-capture space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-lg font-medium">Subscription</h3>
          <p className="text-sm text-muted-foreground">
            Manage your subscription details.
          </p>
        </div>
        <Button onClick={getSession} disabled={loading}>
          Manage Subscription
          {loading && <LoadingSpinner className="mr-0" />}
        </Button>
      </div>
      <Separator />
      <div className="space-y-6">
        {needsPaymentMethod ? (
          <Alert className="border-amber-200 bg-amber-50 text-amber-900">
            <AlertTriangle className="h-4 w-4 text-amber-700" />
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <AlertTitle>
                  Add a payment method to avoid interruption
                </AlertTitle>
                <AlertDescription>
                  Please add a payment method before your trial ends to avoid
                  interruption to your subscription.
                </AlertDescription>
              </div>
              <Button
                className="self-start sm:self-auto"
                onClick={handleAddPaymentMethod}
                disabled={upgradeLoading}
              >
                {upgradeLoading ? (
                  <LoadingSpinner className="mr-0" />
                ) : (
                  'Add payment method'
                )}
              </Button>
            </div>
          </Alert>
        ) : null}
        <Card>
          <CardHeader className="space-y-1">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Current Plan
              </CardTitle>
              {planStatus === 'trialing' && renewalDate ? (
                <span className="inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-900">
                  <Clock className="h-3.5 w-3.5" />
                  {`${renewalLabel} ${format(new Date(renewalDate), 'd MMM')}`}
                </span>
              ) : (
                <Badge variant="outline" className={statusBadgeClass}>
                  {statusLabel}
                </Badge>
              )}
            </div>
            <div className="space-y-1">
              {isBillingLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-6 w-40" />
                  <Skeleton className="h-4 w-28" />
                </div>
              ) : (
                <>
                  <p className="text-xl font-semibold text-foreground">
                    {resolvedPlan.name ??
                      (entitlements?.plan_key
                        ? PLAN_LABELS[entitlements.plan_key]
                        : fallbackPlan.name)}
                  </p>
                  <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                    {discount?.percent_off || discount?.amount_off ? (
                      <>
                        {resolvedPlan.original_amount != null ? (
                          <span className="text-muted-foreground line-through">
                            {formatCurrency(
                              resolvedPlan.original_amount,
                              resolvedPlan.currency ?? fallbackPlan.currency,
                            )}
                          </span>
                        ) : null}
                        <span className="text-foreground font-semibold">
                          {formatCurrency(
                            resolvedPlan.amount ?? fallbackPlan.amount,
                            resolvedPlan.currency ?? fallbackPlan.currency,
                          )}
                        </span>
                      </>
                    ) : (
                      <span>
                        {formatCurrency(
                          resolvedPlan.amount ?? fallbackPlan.amount,
                          resolvedPlan.currency ?? fallbackPlan.currency,
                        )}
                      </span>
                    )}
                    <span>
                      / {resolvedPlan.interval ?? fallbackPlan.interval}
                    </span>
                    {discount?.percent_off ? (
                      <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                        <Tag className="h-3.5 w-3.5" />
                        {discount.percent_off}% OFF
                        {discount.months_remaining != null
                          ? ` (${discount.months_remaining} months remaining)`
                          : discount.duration_in_months
                            ? ` (${discount.duration_in_months} months)`
                            : ''}
                      </span>
                    ) : discount?.amount_off ? (
                      <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                        <Tag className="h-3.5 w-3.5" />
                        {formatCurrency(
                          discount.amount_off / 100,
                          resolvedPlan.currency ?? fallbackPlan.currency,
                        )}{' '}
                        OFF
                      </span>
                    ) : null}
                  </div>
                </>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-0 text-sm text-muted-foreground border-t pt-4">
            {isBillingLoading ? (
              <Skeleton className="h-4 w-36" />
            ) : renewalDate ? (
              <>
                {`${renewalLabel}:`}{' '}
                <span className="text-foreground">
                  {format(new Date(renewalDate), 'MMM d, yyyy')}
                </span>
              </>
            ) : (
              'No upcoming renewal date available.'
            )}
          </CardContent>
        </Card>
        {entitlements ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Plan Usage</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div>
                Clients: {entitlements.client_count}
                {entitlements.max_clients
                  ? ` / ${entitlements.max_clients}`
                  : ' / Unlimited'}
              </div>
              <div>
                Coach seats: {entitlements.coach_seat_count}
                {entitlements.max_coach_seats
                  ? ` / ${entitlements.max_coach_seats}`
                  : ' / Unlimited'}
              </div>
            </CardContent>
          </Card>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Payment Method</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-md bg-muted">
                  <CreditCard className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {isBillingLoading ? (
                      <Skeleton className="h-4 w-40" />
                    ) : paymentMethod?.last4 ? (
                      `${paymentMethod.brand ?? 'Card'} ending in ${paymentMethod.last4}`
                    ) : subscription?.has_payment_method ? (
                      'Payment method on file'
                    ) : (
                      'No payment method added'
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {isBillingLoading ? (
                      <Skeleton className="mt-2 h-3 w-28" />
                    ) : paymentMethod?.exp_month && paymentMethod?.exp_year ? (
                      `Expires ${paymentMethod.exp_month}/${paymentMethod.exp_year}`
                    ) : (
                      'Manage your card details in Stripe'
                    )}
                  </p>
                </div>
              </div>
              <Button
                variant="link"
                className="px-0"
                onClick={getSession}
                disabled={loading}
              >
                {loading ? <LoadingSpinner /> : 'Edit'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Billing History</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={getSession}
              disabled={loading}
            >
              View all
            </Button>
          </CardHeader>
          <CardContent className="pt-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Invoice</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="text-sm text-muted-foreground"
                    >
                      {isBillingLoading ? (
                        <div className="space-y-2">
                          <Skeleton className="h-4 w-full" />
                          <Skeleton className="h-4 w-full" />
                          <Skeleton className="h-4 w-2/3" />
                        </div>
                      ) : (
                        'No billing history available yet.'
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  invoices.map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell>
                        {invoice.created
                          ? format(new Date(invoice.created), 'MMM d, yyyy')
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {invoice.amount_paid != null
                          ? formatCurrency(
                              invoice.amount_paid / 100,
                              invoice.currency ?? resolvedPlan.currency,
                            )
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <InvoiceStatusBadge status={invoice.status ?? '-'} />
                      </TableCell>
                      <TableCell className="text-right">
                        {invoice.hosted_invoice_url || invoice.invoice_pdf ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() =>
                              window.open(
                                invoice.hosted_invoice_url ??
                                  invoice.invoice_pdf ??
                                  '',
                                '_blank',
                              )
                            }
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            -
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

const InvoiceStatusBadge = ({ status }: { status: string }) => {
  const statusClass =
    status === 'paid'
      ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
      : status === 'open'
        ? 'bg-amber-100 text-amber-700 border-amber-200'
        : 'bg-muted text-muted-foreground border-border'

  return (
    <Badge variant="outline" className={`capitalize ${statusClass}`}>
      {status}
    </Badge>
  )
}
